import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUseCaseConfig } from "./useUseCaseConfig";
import FieldBuilder from "./FieldBuilder";
import ChecklistBuilder from "./ChecklistBuilder";
import FormPreview from "./FormPreview";
import {
  UseCaseConfig,
  ReviewSampling,
  FormConfigField,
  ChecklistItem,
  USE_CASE_CATEGORY,
  UseCaseCategoryValue,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_LABELS,
  DataSourceTypeValue,
  COMPARISON_MODE,
  ComparisonModeValue,
  CLAIM_EXPIRY_OPTIONS,
  OPTION_FIELD_TYPES,
  FIELD_TYPE_LABELS,
  makeUseCaseConfig,
} from "../shared/types";
import * as S from "../shared/styles";
import Loader from "@amzn/meridian/loader";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";

// ----------------------------  Tabs  ----------------------------

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "fields", label: "Fields" },
  { key: "checklist", label: "Checklist" },
  { key: "sampling", label: "Review Sampling" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

// ----------------------------  Validation  ----------------------------

interface ConfigErrors {
  fieldErrors: string[];
  checklistErrors: string[];
}

function validateConfig(fields: FormConfigField[], checklist: ChecklistItem[]): ConfigErrors {
  const fieldErrors: string[] = [];
  const checklistErrors: string[] = [];
  if (fields.length === 0) fieldErrors.push("Add at least one field.");
  fields.forEach((f, i) => {
    const tag = "Field " + (i + 1);
    if (!f.label?.trim()) fieldErrors.push(tag + ": label is required.");
    if (OPTION_FIELD_TYPES.indexOf(f.type) !== -1 && f.options.filter(Boolean).length === 0)
      fieldErrors.push(tag + " (" + FIELD_TYPE_LABELS[f.type] + "): needs at least one option.");
    if (f.min != null && f.max != null && f.min > f.max)
      fieldErrors.push(tag + ": min must be \u2264 max.");
    if (f.step != null && f.step <= 0) fieldErrors.push(tag + ": step must be greater than 0.");
    if (f.char_limit != null && f.char_limit <= 0)
      fieldErrors.push(tag + ": character limit must be greater than 0.");
  });
  checklist.forEach((c, i) => {
    if (!c.label?.trim())
      checklistErrors.push("Checklist item " + (i + 1) + ": label is required.");
  });
  return { fieldErrors, checklistErrors };
}

// ----------------------------  Main Component  ----------------------------

const UseCaseEditor: React.FC = () => {
  const { useCaseId } = useParams<{ useCaseId?: string }>();
  const navigate = useNavigate();
  const {
    configs,
    isLoading,
    isError,
    fetchError,
    authError,
    configById,
    saveUseCase,
    updateUseCase,
    isSaving,
    saveSuccess,
    saveError,
    resetSave,
    retryFetch,
  } = useUseCaseConfig();

  const editing = Boolean(useCaseId);

  // Initialise state once configs are loaded (for edit mode).
  const [initialised, setInitialised] = useState(!editing);
  const [tab, setTab] = useState<TabKey>("overview");
  const [uc, setUc] = useState<UseCaseConfig>(() => makeUseCaseConfig());
  const [showSaveError, setShowSaveError] = useState(false);

  // When editing, seed state from the store once loading finishes.
  useEffect(() => {
    if (!editing || initialised || isLoading) return;
    const existing = configById(useCaseId!);
    if (existing) {
      setUc({
        ...existing,
        fields: existing.fields.map((f) => ({ ...f, options: [...f.options] })),
        checklist_items: existing.checklist_items.map((c) => ({ ...c })),
      });
    }
    // Mark initialised even when not found so the not-found branch can render.
    setInitialised(true);
  }, [editing, initialised, isLoading, useCaseId, configById]);

  const setU = (patch: Partial<UseCaseConfig>) => {
    setUc((prev) => ({ ...prev, ...patch }));
    // Dismiss save error when the user modifies the form.
    if (showSaveError) setShowSaveError(false);
  };

  // Clear any stale save state left over from a prior session.
  useEffect(() => {
    resetSave();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect on successful save.
  useEffect(() => {
    if (saveSuccess) {
      resetSave();
      navigate("/audit/config");
    }
  }, [saveSuccess, navigate, resetSave]);

  // Show error banner on save failure; reset slice so user can retry.
  useEffect(() => {
    if (saveError) {
      setShowSaveError(true);
      resetSave();
    }
  }, [saveError, resetSave]);

  // ----------------------------  Validation  ----------------------------

  const nameError = useMemo(() => {
    const name = uc.name?.trim();
    if (!name) return "Name is required.";
    if (name.length > 100) return "Name must be 100 characters or fewer.";
    const clash = configs.some(
      (u) => u.id !== uc.id && u.name.trim().toLowerCase() === name.toLowerCase(),
    );
    if (clash) return "A use case with this name already exists.";
    return null;
  }, [uc.name, uc.id, configs]);

  const { fieldErrors, checklistErrors } = useMemo(
    () => validateConfig(uc.fields, uc.checklist_items),
    [uc.fields, uc.checklist_items],
  );

  // ----------------------------  Sampling helpers  ----------------------------

  const sampling = uc.review_sampling;
  const clampPct = (v: string | number) => Math.max(0, Math.min(100, Number(v) || 0));

  const setSampling = (patch: Partial<ReviewSampling>) => {
    const next: ReviewSampling = { ...sampling, ...patch };
    // Disabling 2nd also disables 3rd.
    if (patch.second_enabled === false) {
      next.third_enabled = false;
    }
    const effSecond = next.second_enabled ? next.second_pct : 0;
    const effThird = next.second_enabled && next.third_enabled ? next.third_pct : 0;
    const maxR = effThird > 0 ? 3 : effSecond > 0 ? 2 : 1;
    setUc((prev) => ({ ...prev, review_sampling: next, inter_rater_count: maxR }));
  };

  const samplingError =
    sampling.second_enabled && sampling.second_pct <= 0
      ? "Enter a 2nd-review % greater than 0, or disable the 2nd review."
      : sampling.second_enabled && sampling.third_enabled && sampling.third_pct <= 0
        ? "Enter a 3rd-review % greater than 0, or disable the 3rd review."
        : sampling.second_enabled &&
            sampling.third_enabled &&
            sampling.third_pct > sampling.second_pct
          ? "The 3rd-review % cannot exceed the 2nd-review %."
          : null;

  const allErrors = [
    ...(nameError ? [nameError] : []),
    ...fieldErrors,
    ...checklistErrors,
    ...(samplingError ? [samplingError] : []),
  ];
  const canSave = allErrors.length === 0;

  const persist = (forceActivate?: boolean) => {
    const ts = new Date().toISOString();
    const toSave: UseCaseConfig = {
      ...uc,
      status: forceActivate ? "active" : uc.status,
      updated_at: ts,
    };
    if (editing) {
      updateUseCase(toSave);
    } else {
      saveUseCase(toSave);
    }
  };

  // ----------------------------  Tab labels with counts  ----------------------------

  const tabLabels: Record<TabKey, string> = {
    overview: "Overview",
    fields: "Fields (" + uc.fields.length + ")",
    checklist: "Checklist (" + uc.checklist_items.length + ")",
    sampling: "Review Sampling",
  };

  // ----------------------------  Loading / Not Found  ----------------------------

  if (editing && (isLoading || !initialised)) {
    return (
      <Row width="fill" alignmentHorizontal="center">
        <Column height="60vh" alignmentVertical="center">
          <Loader />
        </Column>
      </Row>
    );
  }

  if (editing && initialised && isError) {
    return (
      <div style={S.pageWrapper}>
        <div
          style={{
            ...S.devBanner,
            background: "#fee2e2",
            borderColor: "#ef4444",
            color: "#991b1b",
          }}
        >
          {authError
            ? "Unable to resolve your identity. Please refresh the page or try again later."
            : "Failed to load use case configurations."}
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          {fetchError && (
            <button style={S.btnPrimary} onClick={retryFetch}>
              Retry
            </button>
          )}
          <button style={S.btnSecondary} onClick={() => navigate("/audit/config")}>
            Back to list
          </button>
        </div>
      </div>
    );
  }

  if (editing && initialised && !configById(useCaseId!)) {
    return (
      <div style={S.pageWrapper}>
        <div
          style={{
            ...S.devBanner,
            background: "#fee2e2",
            borderColor: "#ef4444",
            color: "#991b1b",
          }}
        >
          Use case not found. It may have been deleted.
        </div>
        <button style={S.btnSecondary} onClick={() => navigate("/audit/config")}>
          Back to list
        </button>
      </div>
    );
  }

  // ----------------------------  Render  ----------------------------

  return (
    <div style={S.pageWrapper}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div>
          <h1 style={S.pageTitle}>{editing ? uc.name || "Edit Use Case" : "New Use Case"}</h1>
          <p style={S.pageSubtitle}>
            Configure settings, audit fields, and checklist — all in one place.
          </p>
        </div>
        <button
          style={{ ...S.btnSecondary, fontSize: "0.875rem", padding: "8px 16px" }}
          onClick={() => navigate("/audit/config")}
        >
          ← All use cases
        </button>
      </div>

      {/* Tabs */}
      <div style={S.tabRow}>
        <div style={S.tabGroup}>
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={tab === t.key ? S.tabActive : S.tabInactive}
            >
              {tabLabels[t.key]}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {tab === "overview" && (
        <div style={{ ...S.configCard, maxWidth: "52rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>
            {/* Name */}
            <div style={S.formRow}>
              <label style={S.formLabel}>
                Use Case Name<span style={S.formLabelRequired}>*</span>
              </label>
              <input
                style={S.formInput}
                value={uc.name}
                onChange={(e) => setU({ name: e.target.value })}
                placeholder="e.g. Safety Genie: Cage Inspection"
              />
              <p style={S.formHelp}>Unique, max 100 characters.</p>
            </div>
            {/* Category */}
            <div style={S.formRow}>
              <label style={S.formLabel}>
                Category<span style={S.formLabelRequired}>*</span>
              </label>
              <select
                style={S.formSelect}
                value={uc.category}
                onChange={(e) => setU({ category: e.target.value as UseCaseCategoryValue })}
              >
                {Object.values(USE_CASE_CATEGORY).map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            {/* Description — full width */}
            <div style={{ ...S.formRow, gridColumn: "1 / -1" }}>
              <label style={S.formLabel}>Description</label>
              <textarea
                style={S.formTextarea}
                value={uc.description}
                onChange={(e) => setU({ description: e.target.value })}
                placeholder="What does this audit evaluate?"
              />
            </div>
            {/* Data Source Type */}
            <div style={S.formRow}>
              <label style={S.formLabel}>
                Data Source Type<span style={S.formLabelRequired}>*</span>
              </label>
              <select
                style={S.formSelect}
                value={uc.data_source_type}
                onChange={(e) => setU({ data_source_type: e.target.value as DataSourceTypeValue })}
              >
                {Object.values(DATA_SOURCE_TYPE).map((v) => (
                  <option key={v} value={v}>
                    {DATA_SOURCE_LABELS[v as DataSourceTypeValue]}
                  </option>
                ))}
              </select>
              <p style={S.formHelp}>Determines the workspace media viewer.</p>
            </div>
            {/* Comparison Mode */}
            <div style={S.formRow}>
              <label style={S.formLabel}>
                Comparison Mode<span style={S.formLabelRequired}>*</span>
              </label>
              <select
                style={S.formSelect}
                value={uc.comparison_mode}
                onChange={(e) => setU({ comparison_mode: e.target.value as ComparisonModeValue })}
              >
                <option value={COMPARISON_MODE.SIMPLE}>Simple — correct / incorrect</option>
                <option value={COMPARISON_MODE.OVERRIDE}>Override — AI vs human</option>
              </select>
            </div>
            {/* Claim Expiry */}
            <div style={S.formRow}>
              <label style={S.formLabel}>
                Claim Expiry<span style={S.formLabelRequired}>*</span>
              </label>
              <select
                style={S.formSelect}
                value={String(uc.claim_expiry_minutes)}
                onChange={(e) => setU({ claim_expiry_minutes: Number(e.target.value) })}
              >
                {CLAIM_EXPIRY_OPTIONS.map((o) => (
                  <option key={o.value} value={String(o.value)}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Active toggle — full width */}
            <div style={{ ...S.formRow, gridColumn: "1 / -1", paddingTop: "4px" }}>
              <label style={{ fontSize: "0.875rem", color: "#374151", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  style={S.formCheckbox}
                  checked={uc.status === "active"}
                  onChange={(e) => setU({ status: e.target.checked ? "active" : "inactive" })}
                />
                Active (available in the audit queue)
              </label>
            </div>
          </div>
          {nameError && <div style={{ ...S.formError, marginTop: "8px" }}>{nameError}</div>}
        </div>
      )}

      {/* Fields Tab — split: builder left, preview right */}
      {tab === "fields" && (
        <div style={splitLayout}>
          <div style={splitLeft}>
            <div style={sectionTitle}>Fields</div>
            <FieldBuilder fields={uc.fields} onChange={(fields) => setU({ fields })} />
            {fieldErrors.length > 0 && (
              <div style={{ ...S.formError, marginTop: "8px" }}>
                {fieldErrors.map((e) => (
                  <div key={e}>• {e}</div>
                ))}
              </div>
            )}
          </div>
          <div style={splitRight}>
            <div style={sectionTitle}>Live Preview</div>
            <div style={previewBox}>
              <FormPreview config={uc} showChecklist={false} />
            </div>
          </div>
        </div>
      )}

      {/* Checklist Tab — split: builder left, preview right */}
      {tab === "checklist" && (
        <div style={splitLayout}>
          <div style={splitLeft}>
            <div style={sectionTitle}>Checklist — at least 1 required</div>
            <ChecklistBuilder
              items={uc.checklist_items}
              onChange={(checklist_items) => setU({ checklist_items })}
            />
            {checklistErrors.length > 0 && (
              <div style={{ ...S.formError, marginTop: "8px" }}>
                {checklistErrors.map((e) => (
                  <div key={e}>• {e}</div>
                ))}
              </div>
            )}
          </div>
          <div style={splitRight}>
            <div style={sectionTitle}>Preview (Pass / Fail / N/A)</div>
            <div style={previewBox}>
              {uc.checklist_items.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {uc.checklist_items.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontSize: "0.875rem",
                      }}
                    >
                      <span style={{ color: "#374151" }}>{item.label || "(untitled item)"}</span>
                      <span style={{ display: "flex", gap: "4px" }}>
                        {["Pass", "Fail", "N/A"].map((r) => (
                          <span
                            key={r}
                            style={{
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              border: "1px solid #e5e7eb",
                              borderRadius: "4px",
                              color: "#9ca3af",
                            }}
                          >
                            {r}
                          </span>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: "0.875rem", color: "#9ca3af" }}>Add items to preview.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Review Sampling Tab */}
      {tab === "sampling" && (
        <div style={{ ...S.configCard, maxWidth: "42rem" }}>
          <div style={sectionTitle}>Review Sampling</div>
          <p style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "16px" }}>
            Most cases get a single review. Enable a tier to sample a percentage of cases for an
            independent 2nd (and 3rd) review to measure inter-rater reliability.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
            {/* 2nd review */}
            <div>
              <label style={samplingCheckLabel}>
                <input
                  type="checkbox"
                  style={S.formCheckbox}
                  checked={!!sampling.second_enabled}
                  onChange={(e) => setSampling({ second_enabled: e.target.checked })}
                />
                <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "#374151" }}>
                  Enable 2nd review
                </span>
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "8px" }}>
                <input
                  type="number"
                  style={{ ...S.formInput, width: "80px" }}
                  value={sampling.second_enabled ? sampling.second_pct : ""}
                  onChange={(e) => setSampling({ second_pct: clampPct(e.target.value) })}
                  placeholder="0"
                  disabled={!sampling.second_enabled}
                  min={0}
                  max={100}
                />
                <span style={{ color: "#6b7280" }}>%</span>
              </div>
              <p style={{ ...S.formHelp, marginTop: "4px" }}>% of cases needing a 2nd review.</p>
              <div style={{ marginTop: "12px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#4b5563",
                    marginBottom: "4px",
                  }}
                >
                  2nd review reviewer
                </label>
                <select
                  style={S.formSelect}
                  value={sampling.second_reviewer}
                  onChange={(e) => setSampling({ second_reviewer: e.target.value })}
                  disabled={!sampling.second_enabled}
                >
                  <option value="">Any authorized auditor</option>
                </select>
              </div>
            </div>

            {/* 3rd review */}
            <div style={{ opacity: sampling.second_enabled ? 1 : 0.5 }}>
              <label
                style={{
                  ...samplingCheckLabel,
                  cursor: sampling.second_enabled ? "pointer" : "not-allowed",
                }}
              >
                <input
                  type="checkbox"
                  style={S.formCheckbox}
                  checked={!!sampling.third_enabled}
                  disabled={!sampling.second_enabled}
                  onChange={(e) => setSampling({ third_enabled: e.target.checked })}
                />
                <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "#374151" }}>
                  Enable 3rd review
                </span>
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "8px" }}>
                <input
                  type="number"
                  style={{ ...S.formInput, width: "80px" }}
                  value={sampling.third_enabled ? sampling.third_pct : ""}
                  onChange={(e) => setSampling({ third_pct: clampPct(e.target.value) })}
                  placeholder="0"
                  disabled={!sampling.second_enabled || !sampling.third_enabled}
                  min={0}
                  max={100}
                />
                <span style={{ color: "#6b7280" }}>%</span>
              </div>
              <p style={{ ...S.formHelp, marginTop: "4px" }}>Must be &le; the 2nd-review %.</p>
              <div style={{ marginTop: "12px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#4b5563",
                    marginBottom: "4px",
                  }}
                >
                  3rd review reviewer
                </label>
                <select
                  style={S.formSelect}
                  value={sampling.third_reviewer}
                  onChange={(e) => setSampling({ third_reviewer: e.target.value })}
                  disabled={!sampling.second_enabled || !sampling.third_enabled}
                >
                  <option value="">Any authorized auditor</option>
                </select>
              </div>
            </div>
          </div>

          {samplingError && <div style={{ ...S.formError, marginTop: "8px" }}>{samplingError}</div>}

          {sampling.second_enabled ? (
            <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "12px" }}>
              Result: ~{100 - (sampling.second_pct || 0)}% single-review, ~
              {(sampling.second_pct || 0) - (sampling.third_enabled ? sampling.third_pct || 0 : 0)}%
              double-review, ~{sampling.third_enabled ? sampling.third_pct || 0 : 0}% triple-review.
            </div>
          ) : (
            <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "12px" }}>
              All cases get a single review (sampling disabled).
            </div>
          )}
        </div>
      )}

      {/* Save error banner */}
      {showSaveError && (
        <div style={saveErrorBanner}>
          <span>Failed to save. Please check your connection and try again.</span>
          <button
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#991b1b",
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
            onClick={() => setShowSaveError(false)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Footer — Save actions */}
      <div style={S.wizardNav}>
        <button style={S.btnSecondary} onClick={() => navigate("/audit/config")}>
          Cancel
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {allErrors.length > 0 && (
            <span style={{ fontSize: "0.75rem", color: "#ef4444" }}>
              {allErrors.length} issue(s) to resolve
            </span>
          )}
          <button
            style={{ ...S.btnOutline, opacity: canSave ? 1 : 0.5 }}
            disabled={!canSave || isSaving}
            onClick={() => persist()}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
          <button
            style={{ ...S.btnPrimary, opacity: canSave ? 1 : 0.5 }}
            disabled={!canSave || isSaving}
            onClick={() => persist(true)}
          >
            {isSaving ? "Saving..." : "Save & Activate"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ----------------------------  Layout Styles  ----------------------------

const splitLayout: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "24px",
  alignItems: "start",
};

const splitLeft: React.CSSProperties = {
  minWidth: 0,
};

const splitRight: React.CSSProperties = {
  minWidth: 0,
  position: "sticky",
  top: "80px",
};

const sectionTitle: React.CSSProperties = {
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "#374151",
  marginBottom: "8px",
};

const previewBox: React.CSSProperties = {
  background: "#f9fafb",
  borderRadius: "12px",
  border: "1px solid #e5e7eb",
  padding: "16px",
};

const saveErrorBanner: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#fee2e2",
  border: "1px solid #fca5a5",
  borderRadius: "8px",
  padding: "12px 16px",
  marginTop: "16px",
  maxWidth: "52rem",
  fontSize: "0.875rem",
  color: "#991b1b",
};

const samplingCheckLabel: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  userSelect: "none",
  marginBottom: "4px",
};

export default UseCaseEditor;
